import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import { useTheme, Theme, TextField, MenuItem } from '@mui/material';
import { Modal, ModalHeader, ModalContent } from '@components/ModalComponents';
import { Arpaymentmethods } from '@gql/base-types';
import StripePaymentMethodForm, { StripeMethodKind } from './StripePaymentMethodForm';

interface MethodAddEditModalProps {
  open: boolean;
  //TODO: Fix this type
  onClose: () => void;
  methodInput?: Arpaymentmethods;
  primaryExists: boolean;
  customerId: number;
}

/** The row's `type` → the kind of method the form saves. Both bank types the portal has written are one kind. */
const kindOfType = (type: string | null | undefined): StripeMethodKind | `` => {
  if (type === `card`) return `card`;
  if (type === `ach` || type === `echeck`) return `bank`;
  return ``;
};

////////// COMPONENT //////////
const MethodAddEditModal: React.FC<MethodAddEditModalProps> = (props: MethodAddEditModalProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { open, onClose, methodInput, primaryExists, customerId } = props;

  const [name, setName] = useState(``);
  const [kind, setKind] = useState<StripeMethodKind | ``>(``);

  useEffect(() => {
    if (methodInput && methodInput.name) setName(methodInput.name);
    if (methodInput && methodInput.type) setKind(kindOfType(methodInput.type));
  }, [methodInput]);

  const handleClose = () => {
    if (onClose) onClose();
    setName(``);
    setKind(``);
  };

  const editing = Boolean(methodInput && methodInput.id !== null && methodInput.id !== undefined);

  return (
    <>
      <Modal open={open}>
        <ModalHeader handleClose={handleClose}>{editing ? `Edit` : `Add`} Payment Method</ModalHeader>

        <ModalContent
          subtitle={
            editing
              ? `Enter the replacement card or bank account below. A replacement bank account is verified through your bank's login; to add one by routing and account number instead, add it as a new payment method. Stripe collects and secures the details; HopDrive keeps only the last four digits and, for a card, the brand and expiration.`
              : `Stripe collects and secures your payment details. HopDrive keeps only the last four digits and, for a card, the brand and expiration. A bank account is verified through your bank's login; if your bank's login can't be completed in Stripe's window, use its "Enter bank details manually" link instead, and Stripe verifies the account with a small deposit or two.`
          }
        >
          <TextField
            fullWidth
            required
            label='Payment Name'
            placeholder='Enter a custom name for this payment...'
            variant='outlined'
            margin='none'
            value={name}
            onChange={event => {
              setName(event.target.value);
            }}
          />
          {name.length > 0 ? (
            <>
              <div className={cls.break} />
              <TextField
                fullWidth
                required
                select
                label='Payment Type'
                placeholder='Select a payment type...'
                variant='outlined'
                margin='none'
                value={kind}
                onChange={event => {
                  setKind(event.target.value as StripeMethodKind | ``);
                }}
              >
                <MenuItem key={'Select Payment Type'} value={``}>
                  <em>Select Payment Type</em>
                </MenuItem>
                <MenuItem key={'Credit Card'} value={`card`}>
                  Credit Card - 3% processing fee
                </MenuItem>
                <MenuItem key={'Bank Account'} value={`bank`}>
                  Bank Account (ACH) - no processing fee
                </MenuItem>

              </TextField>
              {kind !== `` ? (
                <>
                  <div className={cls.break} />
                  <StripePaymentMethodForm
                    key={kind}
                    customerId={customerId}
                    id={methodInput?.id}
                    name={name}
                    kind={kind}
                    primaryExists={primaryExists}
                    handleClose={handleClose}
                  />
                </>
              ) : null}
            </>
          ) : null}
        </ModalContent>
      </Modal>
    </>
  );
};

export default MethodAddEditModal;

////////// STYLES //////////
const useStyles = (theme: Theme) => {
  return {
    break: css`
      width: 100%;
      height: ${theme.spacing(1.5)};
    `,
  };
};
